import {Choice} from 'platform/components';

import {useState} from 'react';

import {isNotNil, uniqBy} from 'ramda';

import type {FeatureResponseBody} from '@dms/api/new-vehicle';

import {useFeatureLibrarySearch} from '../hooks/useFeatureLibrarySearch';
import {t} from '../utils/catalogueUi';

const matchesSearch = (feature: FeatureResponseBody, term: string) =>
  [feature.name, feature.factoryCode, ...feature.codes].some((value) =>
    value?.toLocaleLowerCase().includes(term)
  );

const toOptions = (features: FeatureResponseBody[]) =>
  features.map(({id, name}) => ({value: id, label: name}));

/**
 * Picks one equipment item: the model year's items first, then the rest of the tenant library.
 * The library holds every model's items, so it is searched on the server and only once typed into.
 * A picked `value` stays shown in the control.
 */
export function FeatureLibraryChoice(props: {
  yearFeatures: FeatureResponseBody[];
  excludedIds: string[];
  label: string;
  isLabelVisible?: boolean;
  placeholder: string;
  value?: FeatureResponseBody | null;
  isDisabled?: boolean;
  onPick: (feature: FeatureResponseBody) => void;
}) {
  const [input, setInput] = useState('');
  const term = input.trim();
  const library = useFeatureLibrarySearch(term);
  const yearIds = props.yearFeatures.map(({id}) => id);
  const lowerTerm = term.toLocaleLowerCase();
  const isOffered = (feature: FeatureResponseBody) =>
    !feature.archived && !props.excludedIds.includes(feature.id);
  const yearMatches = props.yearFeatures.filter(
    (feature) => isOffered(feature) && matchesSearch(feature, lowerTerm)
  );
  const libraryMatches = library.features.filter(
    (feature) => isOffered(feature) && !yearIds.includes(feature.id)
  );
  const picked = props.value ? [props.value] : [];
  const isPickedListed = isNotNil(props.value) && matchesSearch(props.value, lowerTerm);
  const isYearFeature = (feature: FeatureResponseBody) => yearIds.includes(feature.id);
  const yearOptions = uniqBy(({id}) => id, [...yearMatches, ...picked.filter(isYearFeature)]);
  const libraryOptions = uniqBy(
    ({id}) => id,
    [...libraryMatches, ...picked.filter((feature) => !isYearFeature(feature))]
  );
  const groups = [
    {label: t('entity.newVehicle.catalogue.modelYear', 'Model year'), features: yearOptions},
    {
      label: t('entity.newVehicle.catalogue.featureLibrary', 'Equipment library'),
      features: libraryOptions,
    },
  ].filter((group) => group.features.length > 0);
  return (
    <Choice<string>
      aria-label={props.label}
      label={props.isLabelVisible ? props.label : undefined}
      value={props.value?.id ?? null}
      isSearchable
      isNotClearable
      isDisabled={props.isDisabled}
      menuInPortal
      placeholder={props.placeholder}
      isLoading={library.isSearching}
      filterOption={(option) => option.value !== props.value?.id || isPickedListed}
      noOptionsMessage={({inputValue}) =>
        inputValue.trim()
          ? t('general.labels.noResultsFound', 'No results found')
          : t(
              'entity.newVehicle.catalogue.typeToSearchLibrary',
              'Type a name or code to search the library'
            )
      }
      options={groups.map((group) => ({label: group.label, options: toOptions(group.features)}))}
      onInputChange={setInput}
      onChange={(id) => {
        const feature = [...yearOptions, ...libraryOptions].find((item) => item.id === id);
        if (feature) {
          props.onPick(feature);
        }
      }}
    />
  );
}
